import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import RegistrationForm from '../components/registration/RegistrationForm';
import { Tour } from '../types';
import { fetchTours } from '../services/tourService';
import { useCustomTours } from '../hooks/useCustomTours';

const RegistrationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get tourId and type from URL params if provided
  const tourIdFromUrl = searchParams.get('tourId');
  const tourType = searchParams.get('type'); // 'custom' or undefined (regular)

  // Use custom tours hook for custom tour data
  const { customTours, loading: customToursLoading, error: customToursError } = useCustomTours();

  useEffect(() => {
    const loadTours = async () => {
      try {
        setLoading(true);
        // Only load regular tours if not a custom tour registration
        if (tourType !== 'custom') {
          const toursData = await fetchTours(50); // Get more tours for the dropdown
          setTours(toursData);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load tours');
      } finally {
        setLoading(false);
      }
    };

    loadTours();
  }, [tourType]);

  const handleRegistrationSuccess = (registrationId: string) => {
    // Could redirect to a success page or show a success message
    console.log('Registration successful:', registrationId);
  };

  const handleRegistrationError = (errorMessage: string) => {
    setError(errorMessage);
  };

  // Determine loading state based on tour type
  const isLoading = tourType === 'custom' ? customToursLoading : loading;
  const hasError = tourType === 'custom' ? customToursError : error;

  if (isLoading) {
    return (
      <div className="min-h-screen gradient-page-neutral flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-deep-blue mx-auto mb-4"></div>
          <p className="body-regular text-secondary-slate">
            {tourType === 'custom' ? 'Loading custom tour details...' : 'Loading tours...'}
          </p>
        </div>
      </div>
    );
  }

  if (hasError && (tourType === 'custom' ? customTours.length === 0 : tours.length === 0)) {
    return (
      <div className="min-h-screen gradient-page-neutral flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
            <AlertCircle size={32} className="text-red-600" />
          </div>
          <h2 className="heading-secondary text-neutral-charcoal mb-4">
            Unable to Load {tourType === 'custom' ? 'Custom Tour' : 'Tours'}
          </h2>
          <p className="body-regular text-secondary-slate mb-6">
            {hasError}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary mr-4"
          >
            Try Again
          </button>
          <button
            onClick={() => navigate('/')}
            className="btn btn-secondary"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-page-neutral">
      {/* Header */}
      <div className="bg-white border-b border-neutral-medium-gray">
        <div className="container py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-neutral-light-gray rounded-lg transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft size={20} className="text-neutral-dark-gray" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-8">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
          >
            <div className="flex items-center gap-2">
              <AlertCircle size={20} className="text-red-600" />
              <p className="body-regular text-red-800">{error}</p>
            </div>
          </motion.div>
        )}

        <RegistrationForm
          tours={tourType === 'custom' ? [] : tours}
          customTours={tourType === 'custom' ? customTours : []}
          selectedTourId={tourIdFromUrl || undefined}
          tourType={(tourType as 'regular' | 'custom') || 'regular'}
          onSuccess={handleRegistrationSuccess}
          onError={handleRegistrationError}
        />
      </div>
    </div>
  );
};

export default RegistrationPage;
